import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import type { WorkingStateWriteInput } from "@/api/admin-api"

import {
  confirmWorkingStateProposal,
  correctWorkingStateFact,
  createWorkingStateFact,
  deleteWorkingStateFact,
  listWorkingState,
  rejectWorkingStateProposal,
  unpinWorkingStateFact,
} from "@/api/admin-api"

import { FAST_STALE_TIME, GC_TIME } from "./_shared"

// Working state (#3023): the panel mutates LIVE server documents, unlike the
// surrounding wizard tabs which edit a draft form — every mutation invalidates
// the listing so the table always shows the persisted truth.
const HOSTED_AGENTS_KEY = ["admin", "hosted-agents"] as const

/** Every working-state fact for one agent (active value, proposals, history). */
export function useAdminHostedAgentWorkingStateQuery(agentId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: [...HOSTED_AGENTS_KEY, agentId, "working-state"],
    queryFn: () => listWorkingState(agentId ?? ""),
    enabled: enabled && !!agentId,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
  })
}

function useInvalidateWorkingState() {
  const queryClient = useQueryClient()
  return async (agentId: string) => {
    await queryClient.invalidateQueries({
      queryKey: [...HOSTED_AGENTS_KEY, agentId, "working-state"],
    })
  }
}

export function useConfirmWorkingStateProposalMutation() {
  const invalidate = useInvalidateWorkingState()
  return useMutation({
    mutationFn: ({
      agentId,
      factId,
      proposalId,
    }: {
      agentId: string
      factId: string
      proposalId: string
    }) => confirmWorkingStateProposal(agentId, factId, proposalId),
    onSuccess: (_fact, { agentId }) => invalidate(agentId),
    // A 409 (pinned fact / concurrent write) leaves the queue as-is; refresh
    // so the admin sees why the confirm was refused.
    onError: (_error, { agentId }) => invalidate(agentId),
  })
}

export function useRejectWorkingStateProposalMutation() {
  const invalidate = useInvalidateWorkingState()
  return useMutation({
    mutationFn: ({
      agentId,
      factId,
      proposalId,
    }: {
      agentId: string
      factId: string
      proposalId: string
    }) => rejectWorkingStateProposal(agentId, factId, proposalId),
    onSuccess: (_fact, { agentId }) => invalidate(agentId),
  })
}

export function useCorrectWorkingStateFactMutation() {
  const invalidate = useInvalidateWorkingState()
  return useMutation({
    mutationFn: ({
      agentId,
      factId,
      input,
    }: {
      agentId: string
      factId: string
      input: WorkingStateWriteInput
    }) => correctWorkingStateFact(agentId, factId, input),
    onSuccess: (_fact, { agentId }) => invalidate(agentId),
  })
}

export function useCreateWorkingStateFactMutation() {
  const invalidate = useInvalidateWorkingState()
  return useMutation({
    mutationFn: ({ agentId, input }: { agentId: string; input: WorkingStateWriteInput }) =>
      createWorkingStateFact(agentId, input),
    onSuccess: (_fact, { agentId }) => invalidate(agentId),
  })
}

export function useUnpinWorkingStateFactMutation() {
  const invalidate = useInvalidateWorkingState()
  return useMutation({
    mutationFn: ({ agentId, factId }: { agentId: string; factId: string }) =>
      unpinWorkingStateFact(agentId, factId),
    onSuccess: (_fact, { agentId }) => invalidate(agentId),
  })
}

export function useDeleteWorkingStateFactMutation() {
  const invalidate = useInvalidateWorkingState()
  return useMutation({
    mutationFn: ({ agentId, factId }: { agentId: string; factId: string }) =>
      deleteWorkingStateFact(agentId, factId),
    onSuccess: (_fact, { agentId }) => invalidate(agentId),
  })
}
